
import React, { useState, useRef, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Slide from "@mui/material/Slide";
import Grow from "@mui/material/Grow";
import { v4 as uuidv4 } from "uuid";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Divider } from "@mui/material";


import SearchService from "services/search-service";
import WorkspaceService from "services/workspace-service";

import { useMaterialUIController } from "context";
import { usePlatformScout } from "context/PlatformScoutContext";

import FacebookResultCard from "./components/FacebookResultCard";
import YouTubeResultCard from "./components/YouTubeResultCard";
import InstagramResultCard from "./components/InstagramResultCard";
import facebookIcon from "assets/images/icons/socials/facebook.png";
import instagramIcon from "assets/images/icons/socials/instagram.png";
import youtubeIcon from "assets/images/icons/socials/youtube.png";

import SuggestCompetitorProfile, { fetchSuggestedProfiles } from "./SuggestCompetitorProfile";
import { Icon } from "@mui/material";

// ---------------- Tooltip styling ----------------
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
    textAlign: "left",
  },
});

const tooltipContent = (
  <div style={{ minWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>How to Use PlatformScout:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Search for competitor profiles across multiple platforms. <br />
      - View key metrics such as followers and verification status. <br />
      - Download profiles to your Social Stash for later analysis.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <strong>Search Tips:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Use exact profile names (<strong>Nike</strong>, <strong>Tesla Motors</strong>). <br />
      - Try exact URL of profile (<strong>https://www.facebook.com/uber</strong>). <br />
      - Avoid generic terms like <strong>"sports"</strong> or <strong>"fashion"</strong>.
    </Typography>
    <Typography variant="body2" gutterBottom>
      <strong>Best Practices:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - Compare profiles to identify industry leaders. <br />
      - Monitor your subscription usage. <br />
      - In the search result, click a profile picture to visit the profile. <br />
      - Upgrading your plan allows more profile downloads.
    </Typography>
  </div>
);

const socialPlatforms = [
  { name: "Facebook", icon: facebookIcon },
  { name: "Instagram", icon: instagramIcon },
  { name: "YouTube", icon: youtubeIcon },
];

// ---------------- Main Component ----------------
export default function SearchCompetitorProfile({ isGuidanceActive, onSearchStart, isWorkflow = false }) {
  const { isAuthenticated } = usePlatformScout(); // Using context for auth availability if needed
  const {
    searchTerm,
    results,
    trendingResults,
    hasSearched,
    error,
    updateSearchState,
    resetSearch,
    clearIfNotPersisted, // [NEW]
    shouldPersist, // [NEW] Read persistence flag
    downloadState // [NEW] Check if download is active
  } = usePlatformScout();

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Local Loading States
  const [loading, setLoading] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(false);

  // Keep social stash & usage local
  const [socialStashProfiles, setSocialStashProfiles] = useState([]);
  const [usage, setUsage] = useState({});
  const [usageRaw, setUsageRaw] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);

  const inputRef = useRef(null);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // ---------------- Initial Loads & Persistence Logic ----------------
  useEffect(() => {
    localStorage.removeItem("fbSLAT");
    fetchUsageData();
    fetchSocialStashProfiles();
    fetchEntitlements();

    // [NEW] Only load trending if we don't have results or trending already
    if (results.length === 0 && trendingResults.length === 0) {
      loadTrendingProfiles();
    }

    // [NEW] Consumer Logic: If we persisted results because of a download,
    // but ALL downloads are now DONE (or no active downloads), we should "consume" the persistence.
    // This ensures that if the user navigates away AGAIN, the results clear (unless they download again).
    const hasActiveDownloads = Object.values(downloadState.items || {}).some(
      (item) => item.isDownloading
    );

    if (shouldPersist && !hasActiveDownloads) {
      updateSearchState({ shouldPersist: false });
    }

    // [NEW] Cleanup on Unmount: Clear results if user didn't download (or if we just consumed the flag)
    return () => {
      clearIfNotPersisted();
    };
  }, []);

  // Sync context searchTerm to local input when mounting
  useEffect(() => {
    // Only update if they differ to avoid loops, and allow clearing (empty string)
    if (searchTerm !== localSearchTerm) {
      setLocalSearchTerm(searchTerm);
    }
  }, [searchTerm]);

  // [NEW] Fetch Entitlements
  const fetchEntitlements = async () => {
    try {
      const data = await WorkspaceService.fetchWorkspaceEntitlements();
      setEntitlements(data);
    } catch (err) {
      console.error("Error fetching entitlements:", err);
    }
  };

  // [NEW] Calculate Permissions
  const FEATURE_KEY = "Competitor Analysis";
  const hasCompetitorAnalysis = entitlements?.features?.includes(FEATURE_KEY) || false;
  // Get the plan name required for this feature (e.g., "Pro") or fallback to "Higher Plan"
  // Note: Backend 'requiredPlans' object maps feature_key -> plan_name
  const requiredPlanName = entitlements?.requiredPlans?.[FEATURE_KEY] || "Professional Plan";


  const fetchSocialStashProfiles = async () => {
    try {
      const workspaceProfiles = await WorkspaceService.fetchWorkspaceProfiles();
      const formatted = workspaceProfiles.map((p) => ({
        id: p.id,
        platform: p.platform,
      }));
      setSocialStashProfiles(formatted);
    } catch (err) {
      console.error("Error fetching Social Stash profiles:", err);
    }
  };

  // [NEW] Fetch Usage Logic
  const fetchUsageData = async () => {
    try {
      setUsageLoading(true);
      const entitlements = await WorkspaceService.fetchWorkspaceEntitlements();

      const planName = entitlements?.plan || "N/A";
      const limits = entitlements?.limits || {};
      const usageStats = entitlements?.usage || {};

      setUsage({
        plan: planName,
        profiles: `${usageStats.profiles || 0} / ${limits.profiles || "N/A"}`,
        profileSwaps: `${usageStats.profile_swaps || 0} / ${limits.profile_swaps || "N/A"}`,
        // Note: usage.snapshots is lifetime, usage.active_snapshots is current count
        scheduledReports: `${usageStats.active_snapshots || 0} / ${limits.snapshots || "N/A"}`,
        activeReports: `${usageStats.active_reports || 0} / ${limits.reports || "N/A"}`,
      });

      // [NEW] Store raw numbers for usage checking
      setUsageRaw({
        profilesUsed: usageStats.profiles || 0,
        profilesLimit: limits.profiles || 0,
        currentPlan: planName
      });

      await new Promise((resolve) => setTimeout(resolve, 400));
    } catch (error) {
      console.error("Error fetching usage details:", error);
    } finally {
      setUsageLoading(false);
    }
  };


  const loadTrendingProfiles = async () => {
    try {
      setTrendingLoading(true);
      // Don't clear local unless refreshing logic changes
      // setTrendingResults([]); 

      // Pass callback for live updates
      await fetchSuggestedProfiles((updatedResults) => {
        // [NEW] Update Context
        updateSearchState({ trendingResults: updatedResults });
      });

    } catch (error) {
      console.error("Error loading trending profiles:", error);
    } finally {
      setTrendingLoading(false);
    }
  };

  // ---------------- Search Logic ----------------
  const [progress, setProgress] = useState(0);
  const wsRef = useRef(null);
  const completedPlatforms = useRef(new Set());
  // Helper for tracking results locally during stream to avoid stale closures
  const accumulatedResultsRef = useRef([]);

  const combineResults = (existing, newItems) => {
    const existingIds = new Set(existing.map((r) => `${r.platform}-${r.id}`));
    const filtered = newItems.filter(
      (item) => !existingIds.has(`${item.platform}-${item.id}`)
    );
    return [...existing, ...filtered];
  };

  const handleSearch = async (event) => {
    if (event.type === "click" || event.key === "Enter") {
      if (!localSearchTerm.trim()) {
        updateSearchState({ error: "Search term cannot be empty." });
        return;
      }

      // [NEW] Dismiss guidance overlay if active
      if (onSearchStart) {
        onSearchStart();
      }

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const clientId = `client-${Date.now()}`;
      const wsBaseUrl = process.env.REACT_APP_API_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${wsBaseUrl}/ws?clientId=${clientId}`);
      wsRef.current = ws;

      // Reset state for new search
      updateSearchState({
        searchTerm: localSearchTerm,
        results: [],
        error: "",
        hasSearched: true
      });
      accumulatedResultsRef.current = []; // Reset local buffer

      setLoading(true);
      setProgress(0);
      completedPlatforms.current.clear();

      ws.onopen = async () => {
        try {
          await SearchService.searchAllPlatforms(localSearchTerm, null, false, clientId);
        } catch (err) {
          console.error("❌ Search initiation failed:", err);
          updateSearchState({ error: "Search request failed to start." });
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type !== "search") return;

          if (data.progress !== undefined) {
            setProgress(Math.floor(data.progress));
          }

          if (["progress", "completed"].includes(data.status) && Array.isArray(data.data)) {
            // Update local ref first
            accumulatedResultsRef.current = combineResults(accumulatedResultsRef.current, data.data);
            // Sync to context
            updateSearchState({ results: [...accumulatedResultsRef.current] });
          }

          if (["completed", "error"].includes(data.status) && data.platform) {
            completedPlatforms.current.add(data.platform);
            console.log(`✅ ${data.platform} marked as finished (${data.status}).`);

            if (data.status === "error") {
              const msg = data.message || `${data.platform} search failed.`;
              // Simple error update
              updateSearchState({ error: msg });
            }

            const allPlatforms = ["Facebook", "YouTube", "Instagram"];
            const allDone = allPlatforms.every((p) => completedPlatforms.current.has(p));

            if (allDone) {
              console.log("🎯 All platform searches finished.");
              setProgress(100);
              setLoading(false);
              ws.close();
            }
          }
        } catch (err) {
          console.warn("⚠️ Invalid WebSocket message:", event.data);
        }
      };

      ws.onerror = (err) => {
        console.error("❌ WebSocket error:", err);
        updateSearchState({ error: "Connection error. Please retry." });
        setLoading(false);
      };

      ws.onclose = () => {
        console.log("🔌 WebSocket closed");
        wsRef.current = null;
      };
    }
  };



  const isProfileInSocialStash = (result) =>
    socialStashProfiles.some(
      (profile) =>
        String(profile.id) === String(result.id) &&
        profile.platform === result.platform
    );

  const renderPlatformCard = (result) => {
    const isInStash = isProfileInSocialStash(result);

    // [NEW] Props to control access
    const accessProps = {
      canDownload: hasCompetitorAnalysis,
      featureName: "Competitor Analysis",
      planRequired: requiredPlanName,
    };

    switch (result.platform) {
      case "Facebook":
        return (
          <FacebookResultCard
            key={result.id}
            result={result}
            isInSocialStash={isInStash}
            fetchUsageData={fetchUsageData}
            // [NEW] Props for Upgrade Checks
            canDownload={hasCompetitorAnalysis}
            featureName={FEATURE_KEY}
            planRequired={requiredPlanName}
            usageRaw={usageRaw}
            isWorkflow={isWorkflow}
          />
        );
      case "YouTube":
        return (
          <YouTubeResultCard
            key={result.id}
            result={result}
            isInSocialStash={isInStash}
            fetchUsageData={fetchUsageData}
            canDownload={hasCompetitorAnalysis}
            featureName={FEATURE_KEY}
            planRequired={requiredPlanName}
            usageRaw={usageRaw}
            isWorkflow={isWorkflow}
          />
        );
      case "Instagram":
        return (
          <InstagramResultCard
            key={result.id}
            result={result}
            isInSocialStash={isInStash}
            fetchUsageData={fetchUsageData}
            canDownload={hasCompetitorAnalysis}
            featureName={FEATURE_KEY}
            planRequired={requiredPlanName}
            usageRaw={usageRaw}
            isWorkflow={isWorkflow}
          />
        );
      default:
        return null;
    }
  };

  // ---------------- Render ----------------
  const isLiveDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
  const hasGuidanceShown = sessionStorage.getItem("platform_scout_intro_shown") === "true"; // [NEW]
  const isDemoHighlight = isLiveDemo && !hasSearched && results.length === 0 && !hasGuidanceShown; // [UPDATED]

  return (
    <Card>
      <MDBox>
        {/* Header */}
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          flexDirection="row"
        >
          <MDTypography variant="h6" color="white">
            Search Competitor Profiles
          </MDTypography>
          <CustomTooltip title={tooltipContent} placement="bottom" arrow>
            <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </CustomTooltip>
        </MDBox>

        {/* Search Box */}
        <MDBox pt={3} pb={3} px={2}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={10}>
              <MDBox
                display="flex"
                gap={2}
                px={3}
                py={3}
                borderRadius="16px"
                sx={{
                  backgroundColor: "#f0f2f5",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                  zIndex: isGuidanceActive ? 1251 : 1, // [NEW] Lift above backdrop (1200)
                  boxShadow: isGuidanceActive ? "0 0 0 4px #FFB347" : "none", // Optional: Highlight ring
                  transition: "z-index 0s, box-shadow 0.3s ease",
                }}
              >
                <MDBox sx={{ position: "relative", flex: 1 }}>

                  <MDInput
                    type="text"
                    placeholder="Try BBC..."
                    fullWidth
                    value={localSearchTerm}
                    onChange={(e) => setLocalSearchTerm(e.target.value)}
                    onKeyDown={handleSearch}
                    ref={inputRef}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: "12px",
                        backgroundColor: "white !important",
                        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                        paddingLeft: "6px",
                      },
                    }}
                  />
                </MDBox>
                <MDButton
                  variant="contained"
                  color="accent"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{ whiteSpace: "nowrap", minWidth: "130px" }}
                >
                  {loading ? "SEARCHING..." : "SEARCH"}
                </MDButton>
              </MDBox>

              {error && (
                <MDBox mt={4} display="flex" justifyContent="center">
                  <MDTypography variant="subtitle2" color="error">
                    {error}
                  </MDTypography>
                </MDBox>
              )}
            </Grid>
          </Grid>

          {/* Usage Info */}
          <MDBox mt={2} textAlign="center">
            <MDTypography variant="subtitle2" mb={1}>
              Usage Information
            </MDTypography>
            <MDBox display="inline-block" overflow="hidden" height="auto">
              <Slide
                direction="up"
                in={!usageLoading}
                mountOnEnter
                unmountOnExit
                timeout={500}
              >
                <MDBox>
                  <MDTypography variant="subtitle2" color="text">
                    <strong>Plan:</strong> {usage.plan} |{" "}
                    <strong>Profiles:</strong> {usage.profiles} |{" "}
                    <strong>Profile Swaps:</strong> {usage.profileSwaps}
                    {entitlements?.features?.includes("Snapshot") && (
                      <>
                        {" "}| <strong>Active Snapshots:</strong>{" "}
                        {usage.scheduledReports}
                      </>
                    )}
                    {entitlements?.features?.includes("Report") && (
                      <>
                        {" "}| <strong>Active Reports:</strong>{" "}
                        {usage.activeReports}
                      </>
                    )}
                  </MDTypography>
                </MDBox>
              </Slide>

              {usageLoading && (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="24px"
                >
                  <CircularProgress size={24} color="accent" />
                </MDBox>
              )}
            </MDBox>
          </MDBox>

          <MDBox
            m={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            px={3}
            py={3}
            borderRadius="16px"
            sx={{
              backgroundColor: "#f0f2f5",
              justifyContent: "center",
              alignItems: "center",
            }}>


            {/* Search Results */}
            {loading && results.length === 0 ? (
              <MDBox mt={4} display="flex" justifyContent="center">
                <CircularProgress color="accent" />
              </MDBox>
            ) : results.length > 0 ? (
              <MDBox m={2}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  textAlign="center"
                  mb={2}
                  sx={{
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.8,
                  }}
                >
                  {/* <Icon sx={{ fontSize: "1.2rem", color: "#116256" }}>search</Icon> */}
                  🔍 Search Results
                </MDTypography>
                {/* Progress Indicator */}
                {loading && (
                  <MDBox m={2} display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress color="accent" />
                    <MDTypography mt={1} variant="caption" color="text.secondary">
                      {progress < 100
                        ? `Fetching results... ${progress}%`
                        : "Finalising results..."}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={3}>
                  {results.map((result) => (
                    <MDBox
                      key={`${result.platform}-${result.id}`}
                      display="flex"
                      justifyContent="center"
                    >
                      {renderPlatformCard(result)}
                    </MDBox>
                  ))}
                </MDBox>
              </MDBox>
            ) : (
              // ✅ Only show error if all platforms failed (no results at all)
              !loading && hasSearched && results.length === 0 && (
                <MDBox mt={4} display="flex" justifyContent="center">
                  <MDTypography variant="subtitle2" color="error">
                    No results found for "{localSearchTerm}" across all platforms.
                  </MDTypography>
                </MDBox>
              )
            )}

            {/* 🔹 Divider between sections */}

            {(hasSearched &&
              <Divider
                variant="middle"
                sx={{
                  width: "100%",
                  my: 4,
                  borderColor: "#116256",
                  borderBottomWidth: 3, // makes it thicker
                  opacity: 0.8,         // slight transparency looks cleaner
                }}
              />
            )}
            {/* Trending Section */}
            <MDBox m={2}>
              <MDTypography
                // variant="subtitle1"
                textAlign="center"
                mb={2}
                // color="text.primary"
                variant="h6"
                fontWeight="bold"
                sx={{
                  fontSize: "0.95rem",
                }}
              >
                🔥 Suggested Profiles
              </MDTypography>
              {trendingLoading ? (
                <MDBox display="flex" justifyContent="center">
                  <CircularProgress color="accent" />
                </MDBox>
              ) : (
                <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={3}>
                  {trendingResults.map((result) => (
                    <MDBox key={result.id} display="flex" justifyContent="center">
                      {renderPlatformCard(result)}
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </MDBox>

          </MDBox>

          {/* Supported Platforms */}
          <MDBox mt={4} textAlign="center">
            <MDTypography variant="subtitle2">
              We support the following platforms
            </MDTypography>
            <Grid container spacing={2} justifyContent="center" mt={2}>
              {socialPlatforms.map((platform, index) => (
                <Grow
                  in={true}
                  timeout={index * 750}
                  key={platform.name}
                  style={{ transformOrigin: "0 0 0" }}
                >
                  <Grid item key={platform.name} sx={{ position: "relative" }}>

                    <img
                      src={platform.icon}
                      alt={platform.name}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        padding: "5px",
                        backgroundColor: "#f8f9fa",
                      }}
                    />
                  </Grid>
                </Grow>
              ))}
            </Grid>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}